import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2, MapPin, HelpCircle } from 'lucide-react';
import { NotificationHelpSheet } from './NotificationHelpSheet';
import { isIOS, isStandalone } from '../utils/pwaInstall';
import { toast } from 'sonner';
import {
  isPushSupported,
  getPermission,
  hasActiveSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
  getLocalTimezone,
} from '../utils/pushClient';

// Members pick their own time for the daily ones (native phone time picker); morning/evening reflection
// reminders + Quiet Hours live in the Reflection reminders card below this one.
const REMINDERS = [
  { id: 'manifest', time: '08:00', label: 'Daily Manifest affirmation', timed: true },
  { id: 'midday', time: '12:00', label: 'Mid-Day Breathing', timed: true },
  { id: 'sleep', time: '21:00', label: 'A Peaceful Sleep', timed: true },
  { id: 'circle-digest', time: '18:00', label: 'Weekly Circle Digest (Sun)', fixed: 'Sundays · 6:00 PM' },
  { id: 'updates', time: null, label: 'App updates & new journeys', fixed: 'Occasional' },
];

const formatHHMM = (hhmm) => {
  const [h, m] = (hhmm || '00:00').split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const prettyTimezone = (tz) => {
  // e.g. "America/Los_Angeles" -> "Los Angeles · PT (auto-adjusts for DST)"
  try {
    const parts = tz.split('/');
    const city = (parts[parts.length - 1] || '').replace(/_/g, ' ');
    const shortTz = new Intl.DateTimeFormat('en', {
      timeZoneName: 'short',
      timeZone: tz,
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value || '';
    return `${city}${shortTz ? ` · ${shortTz}` : ''}`;
  } catch {
    return tz;
  }
};

const PushNotificationSettings = () => {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tz] = useState(getLocalTimezone());
  const [disabled, setDisabled] = useState(new Set());
  const [times, setTimes] = useState({});
  const [help, setHelp] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchPrefs = async () => {
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/push/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDisabled(new Set(data.disabled || []));
        setTimes(data.times || {});
      }
    } catch (e) {
      console.warn('Failed to fetch push prefs', e);
    }
  };

  useEffect(() => {
    if (!isPushSupported()) {
      setSupported(false);
      return;
    }
    setPermission(getPermission());
    hasActiveSubscription().then((is) => {
      setActive(is);
      if (is) fetchPrefs();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleReminder = async (id) => {
    if (!active) return;
    const next = new Set(disabled);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setDisabled(next); // optimistic
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/push/preferences`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ disabled: Array.from(next) }),
      });
      if (!res.ok) throw new Error();
    } catch {
      // Revert
      setDisabled(disabled);
      toast.error('Could not save preference');
    }
  };

  const saveTime = async (id, value) => {
    if (!active || !value) return;
    const prev = times;
    const next = { ...times, [id]: value };
    setTimes(next);
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/push/preferences`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ disabled: Array.from(disabled), times: next }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Reminder set for ${formatHHMM(value)}`);
    } catch {
      setTimes(prev);
      toast.error('Could not save that time');
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setActive(true);
      setPermission('granted');
      await fetchPrefs();
      toast.success('Daily reminders enabled 🔔');
    } catch (e) {
      if (e.message === 'Permission denied' || getPermission() === 'denied') setHelp('blocked');
      else toast.error(e.message || 'Could not enable notifications');
      setPermission(getPermission());
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setActive(false);
      toast.success('Reminders turned off');
    } catch (e) {
      toast.error('Could not disable notifications');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const ok = await sendTestPush();
      if (ok) toast.success('Test push sent 📬 check your device');
      else toast.error('Could not send test');
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    const iosInstall = isIOS() && !isStandalone();
    return (
      <div className="bg-[#FFFCF3] border border-stone-200 rounded-2xl shadow-md p-4" data-testid="push-settings">
        <div className="flex items-start gap-3">
          <BellOff className="w-5 h-5 text-stone-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-stone-800">{iosInstall ? 'One more step on iPhone' : 'Reminders unavailable in this browser'}</h3>
            <p className="text-xs text-stone-500 mt-1">
              {iosInstall
                ? 'Apple only allows notifications from apps on your Home Screen. Add Reflections (Share → Add to Home Screen), open it from there, and this switch turns on.'
                : 'This browser can\'t deliver notifications. Open Reflections in Safari (iPhone) or Chrome (Android).'}
            </p>
            <button type="button" onClick={() => setHelp('install')} className="mt-3 h-10 px-4 rounded-full btn-gold text-xs font-semibold inline-flex items-center gap-1.5" data-testid="push-settings-help">
              <HelpCircle className="w-4 h-4" /> Show me how
            </button>
          </div>
        </div>
        <NotificationHelpSheet open={!!help} mode="install" onClose={() => setHelp(null)} />
      </div>
    );
  }

  const blocked = permission === 'denied';

  return (
    <div className="bg-[#FFFCF3] border border-stone-200 rounded-2xl shadow-md p-4" data-testid="push-settings">
      {!active && !blocked && (
        <div className="mb-4 rounded-xl bg-[#0f1e33] text-[#f9f4ea] p-4" data-testid="push-optin-invite">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#D6AD60]">Daily reminders</p>
          <h3 className="mt-1 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Build a daily habit of peace</h3>
          <ul className="mt-2 space-y-1 text-xs text-[#e3d2a8]/90">
            <li>• A gentle nudge for your next journey day — morning or evening, your times</li>
            <li>• Quiet Hours respected; one reminder, never a flood</li>
            <li>• Occasional news when Rowdy records something new (optional)</li>
          </ul>
          <p className="mt-2 text-[11px] text-[#e3d2a8]/70">You stay in control — your browser will ask for permission first, and you can switch any reminder off here at any time.</p>
        </div>
      )}
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#D6AD60]/20' : 'bg-[#E8DDCB]'}`}>
          <Bell className={`w-5 h-5 ${active ? 'text-[#AA8347]' : 'text-stone-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
            Daily reminders
          </h3>
          <p className="text-xs text-stone-500 mt-0.5">
            {active
              ? 'You are receiving gentle nudges on your schedule.'
              : 'Get today\'s affirmation at 8 AM plus breathing nudges through the day, in your local time.'}
          </p>
        </div>
      </div>

      {/* Timezone badge */}
      <div className="flex items-center gap-2 bg-[#D6AD60]/10 border border-[#D6AD60]/20 rounded-xl px-3 py-2 mb-3" data-testid="push-tz-badge">
        <MapPin className="w-3.5 h-3.5 text-[#AA8347] flex-shrink-0" />
        <span className="text-[11px] text-[#D6AD60]">
          Detected timezone: <span className="font-semibold text-stone-800">{prettyTimezone(tz)}</span>
        </span>
      </div>

      {/* Schedule list */}
      <div className="bg-[#E8DDCB] rounded-xl p-3 mb-3">
        <ul className="space-y-1">
          {REMINDERS.map((r) => {
            const isOff = disabled.has(r.id);
            return (
              <li key={r.id} className="flex items-center justify-between py-1.5 gap-2">
                <div className="flex-1 min-w-0">
                  <p className={`text-xs ${isOff ? 'text-stone-400 line-through' : 'text-stone-800'}`}>{r.label}</p>
                  {r.timed ? (
                    <label className="mt-1 inline-flex items-center gap-1.5 text-[10px] text-stone-500">
                      <span>Every day at</span>
                      <input
                        type="time"
                        value={times[r.id] || r.time}
                        disabled={!active || isOff}
                        onChange={(e) => saveTime(r.id, e.target.value)}
                        className="h-7 px-2 rounded-lg bg-[#FFFCF3] border border-[#D6AD60]/40 text-[11px] font-semibold text-stone-800 disabled:opacity-50"
                        data-testid={`push-time-${r.id}`}
                      />
                    </label>
                  ) : (
                    <p className="font-mono text-[10px] text-stone-500 mt-0.5">{r.fixed}</p>
                  )}
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!isOff}
                  onClick={() => toggleReminder(r.id)}
                  disabled={!active}
                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                    active ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                  } ${!isOff ? 'bg-gradient-to-r from-[#D6AD60] to-[#D6AD60]' : 'bg-[#DBC9A3]'}`}
                  data-testid={`push-toggle-${r.id}`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-[#FFFCF3] rounded-full shadow-sm transition-transform ${
                      !isOff ? 'translate-x-[22px]' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
        <p className="text-[10px] text-stone-500 mt-2 italic">
          {active ? 'Tap a time to change it — reminders arrive in your local time zone. Morning & evening reflection reminders are set below.' : 'Turn on notifications first, then pick your own times.'}
        </p>
      </div>

      {blocked && !active ? (
        <div className="text-xs text-red-500 bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-2 flex items-center justify-between gap-2" data-testid="push-blocked-msg">
          <span>Notifications are blocked for Reflections on this device.</span>
          <button type="button" onClick={() => setHelp('blocked')} className="shrink-0 h-8 px-3 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-[11px] font-semibold" data-testid="push-blocked-help">Show me how</button>
        </div>
      ) : null}

      <div className="flex gap-2">
        {active ? (
          <>
            <button
              onClick={disable}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl bg-[#E8DDCB] hover:bg-[#DBC9A3] text-stone-800 text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="push-disable-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Turn off'}
            </button>
            <button
              onClick={sendTest}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl btn-gold text-sm font-semibold transition-colors disabled:opacity-50"
              data-testid="push-test-btn"
            >
              Send test
            </button>
          </>
        ) : (
          <button
            onClick={enable}
            disabled={busy || blocked}
            className="w-full py-2.5 rounded-xl btn-gold text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="push-enable-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Turn on notifications
          </button>
        )}
      </div>
      <NotificationHelpSheet open={!!help} mode={help || 'install'} onClose={() => setHelp(null)} />
    </div>
  );
};

export default PushNotificationSettings;
